/**
 * Browse cars — redesigned split-screen layout (Iter 67k).
 *
 * Two-pane: left = sticky filter bar + result cards stack; right (lg+) = sticky
 * always-visible map kept in sync with the filtered list. On mobile/tablet
 * the map collapses into a toggleable view via the existing list/map switch.
 *
 * Filters still drive the URL so links + the new Modify-search pill keep working.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import usePageMeta from "@/lib/usePageMeta";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BrowseCarCard from "@/components/browse/BrowseCarCard";
import BrowseFilterStrip from "@/components/browse/BrowseFilterStrip";
import BrowseMap from "@/components/browse/BrowseMap";
import BrowseCountrySummary, { _cityFromLocation, _countryFromLocation } from "@/components/browse/BrowseCountrySummary";
import QuickFilterRail, { QUICK_PRESET_BY_ID } from "@/components/browse/QuickFilterRail";
import ModifySearchPill from "@/components/car/ModifySearchPill";
import RecentVendorsStrip from "@/components/RecentVendorsStrip";
import useSocialProof from "@/lib/useSocialProof";
import { LayoutGrid, Map as MapIcon, Sparkles, List as ListIcon, MapPin as MapPinIcon, SlidersHorizontal, Store } from "lucide-react";
import Money from "@/components/Money";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { X as XIcon } from "lucide-react";
import { readSourceVendor, clearSourceVendor } from "@/lib/sourceVendor";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const SORT_OPTIONS = [
  { id: "price_asc",  label: "Price ↑" },
  { id: "price_desc", label: "Price ↓" },
  { id: "rating_desc", label: "Top rated" },
];

export default function Browse() {
  const { t } = useTranslation();
  usePageMeta("meta.browse.title", "meta.browse.description");
  const [params, setParams] = useSearchParams();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(params.get("view") === "map" ? "map" : "list");
  const [sort, setSort] = useState(params.get("sort") || "price_asc");
  const [hoveredId, setHoveredId] = useState(null);
  const [quickPreset, setQuickPreset] = useState(params.get("quick") || null);

  // Iter 69 — vendor-scope ribbon. When the URL carries `?vendor_id=…`
  // (most commonly because the visitor arrived from `/v/:slug` and the
  // Header's "Browse Cars" link auto-passed the vendor scope), show a
  // dismissable amber ribbon naming the vendor + offering a one-click
  // escape to "Show all vendors". Vendor name comes from localStorage
  // (set on microsite visit) so we don't need an extra round-trip.
  const [sourceVendor, setSourceVendor] = useState(() => readSourceVendor());
  const vendorScopeId = params.get("vendor_id");
  const clearVendorScope = useCallback(() => {
    clearSourceVendor();
    try { window.dispatchEvent(new CustomEvent("rentalcar:source-vendor-changed")); } catch { /* ignore */ }
    setSourceVendor(null);
    const next = new URLSearchParams(params);
    next.delete("vendor_id");
    setParams(next, { replace: true });
  }, [params, setParams]);

  const [pickupLoc, setPickupLoc] = useState(params.get("pickup_location") || "");
  const [dropoffLoc, setDropoffLoc] = useState(params.get("dropoff_location") || "");
  const [startDate, setStartDate] = useState(toLocalInput(params.get("start_date")));
  const [endDate, setEndDate] = useState(toLocalInput(params.get("end_date")));
  const [carType, setCarType] = useState(params.get("car_type") || "");
  const [trans, setTrans] = useState(params.get("transmission") || "");
  const [fuel, setFuel] = useState(params.get("fuel_type") || "");
  const [seats, setSeats] = useState(params.get("seats") ? Number(params.get("seats")) : 0);
  const [price, setPrice] = useState([
    Number(params.get("min_price")) || 0,
    Number(params.get("max_price")) || 500,
  ]);
  // Iter 67bs — boolean traveller-policy filters.
  const [petFriendly, setPetFriendly] = useState(params.get("pet_friendly") === "true");
  const [smokingAllowed, setSmokingAllowed] = useState(params.get("smoking_allowed") === "true");
  // Iter 70b — "Featured only" chip; toggling adds `featured=1` to the URL
  // so the state survives share-links and back-forward navigation.
  const [featuredOnly, setFeaturedOnly] = useState(params.get("featured") === "1");

  // Iter 67bs — Partner-referral attribution. When the URL carries `?ref=CODE`
  // (passed in by a hotel/agency link), we stash the code in localStorage and
  // verify it with the public lookup so we can show a "Booking on behalf of
  // <partner>" ribbon. The code rides along on checkout (see CarDetail.jsx).
  const [partnerRef, setPartnerRef] = useState(null);
  useEffect(() => {
    // Iter 67fg — localStorage key was renamed from `drivenest_partner_ref` to
    // `rentalcar_partner_ref` during the RentalCar.al rebrand. Read both keys
    // so existing users keep their referral attribution; new writes only use
    // the rentalcar key.
    const REF_KEY = "rentalcar_partner_ref";
    const LEGACY_REF_KEY = "drivenest_partner_ref";
    const code = (params.get("ref") || "").trim();
    if (!code) {
      try {
        const stored = localStorage.getItem(REF_KEY) || localStorage.getItem(LEGACY_REF_KEY);
        if (stored) {
          api.get(`/referrals/${encodeURIComponent(stored)}`)
            .then((r) => setPartnerRef(r.data))
            .catch(() => {
              localStorage.removeItem(REF_KEY);
              localStorage.removeItem(LEGACY_REF_KEY);
            });
        }
      } catch { /* ignore */ }
      return;
    }
    api.get(`/referrals/${encodeURIComponent(code)}`)
      .then((r) => {
        setPartnerRef(r.data);
        try { localStorage.setItem(REF_KEY, code); } catch { /* ignore */ }
        // Clear the legacy key once we've written the new one so we don't keep
        // two stale copies around.
        try { localStorage.removeItem(LEGACY_REF_KEY); } catch { /* ignore */ }
      })
      .catch(() => {
        try { localStorage.removeItem(REF_KEY); } catch { /* ignore */ }
        try { localStorage.removeItem(LEGACY_REF_KEY); } catch { /* ignore */ }
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCars = useCallback(async (sortValueOrOverrides = sort, extraOverrides = {}) => {
    // Iter 70b — accept either a plain sort string (back-compat with existing
    // callers) or an overrides object so `apply({featuredOnly: true})` can
    // pass fresh state through without waiting for React to flush setState.
    const overrides = typeof sortValueOrOverrides === "object" && sortValueOrOverrides !== null
      ? sortValueOrOverrides
      : { sort: sortValueOrOverrides, ...extraOverrides };
    const _pickupLoc      = overrides.pickupLoc      ?? pickupLoc;
    const _dropoffLoc     = overrides.dropoffLoc     ?? dropoffLoc;
    const _startDate      = overrides.startDate      ?? startDate;
    const _endDate        = overrides.endDate        ?? endDate;
    const _carType        = overrides.carType        ?? carType;
    const _trans          = overrides.trans          ?? trans;
    const _fuel           = overrides.fuel           ?? fuel;
    const _seats          = overrides.seats          ?? seats;
    const _price          = overrides.price          ?? price;
    const _petFriendly    = overrides.petFriendly    ?? petFriendly;
    const _smokingAllowed = overrides.smokingAllowed ?? smokingAllowed;
    const _featuredOnly   = overrides.featuredOnly   ?? featuredOnly;
    const _sort           = overrides.sort           ?? sort;

    setLoading(true);
    const q = {};
    if (_pickupLoc) q.pickup_location = _pickupLoc;
    if (_dropoffLoc) q.dropoff_location = _dropoffLoc;
    if (_startDate) q.start_date = new Date(_startDate).toISOString();
    if (_endDate) q.end_date = new Date(_endDate).toISOString();
    if (_carType) q.car_type = _carType;
    if (_trans) q.transmission = _trans;
    if (_fuel) q.fuel_type = _fuel;
    if (_seats) q.seats = _seats;
    if (_price[0] > 0) q.min_price = _price[0];
    if (_price[1] < 500) q.max_price = _price[1];
    if (_petFriendly) q.pet_friendly = true;
    if (_smokingAllowed) q.smoking_allowed = true;
    if (_featuredOnly) q.featured = true;
    if (_sort) q.sort = _sort;
    // Iter 67ac: when the URL carries `?vendor_id=…` (e.g. clicking a vendor
    // logo on the home-page carousel) restrict the listing to that host's
    // fleet.
    const vendorId = params.get("vendor_id");
    if (vendorId) q.vendor_id = vendorId;
    // Iter 67co — NOTE: we intentionally do NOT pass `country` to the backend
    // here. The country filter is applied client-side in `visibleCars` so the
    // BrowseCountrySummary chip strip can always render the full unfiltered
    // distribution and let users hop between countries in one click.
    try {
      const { data } = await api.get("/cars", { params: q });
      setCars(data);
    } finally {
      setLoading(false);
    }
  }, [pickupLoc, dropoffLoc, startDate, endDate, carType, trans, fuel, seats, price, petFriendly, smokingAllowed, featuredOnly, sort, params]);

  useEffect(() => { fetchCars(); /* first mount with URL params */ }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Iter 67cp — Geo-IP auto-suggest. On first visit (no explicit `country=`
  // in the URL, no remembered "user already dismissed" flag in sessionStorage)
  // we ask the backend for the renter's country, and if it matches a country
  // we actually serve cars from, pre-apply the chip filter with `?geo=1` in
  // the URL so the banner stays visible across refreshes / strict-mode mounts
  // without any extra state coordination.
  useEffect(() => {
    if (params.get("country")) return;            // user already has an explicit filter
    if (params.get("vendor_id")) return;          // vendor-scoped view — geo would be confusing
    try {
      if (sessionStorage.getItem("rentalcar_geo_applied_or_dismissed") === "1") return;
    } catch { /* sessionStorage blocked → just try the lookup */ }
    let cancelled = false;
    api.get("/public/geo-country")
      .then((r) => {
        if (cancelled || !r.data?.country) return;
        const country = String(r.data.country).trim();
        applyGeoIfPossible(country);
      })
      .catch(() => { /* swallow — geo is best-effort */ });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyGeoIfPossible = useCallback((country) => {
    if (!country || !cars || cars.length === 0) {
      if (country) setPendingGeoCountry(country);
      return;
    }
    const exists = cars.some((c) => {
      const loc = c.location || "";
      const idx = loc.lastIndexOf(",");
      const cName = idx === -1 ? "Other" : loc.slice(idx + 1).trim();
      return cName === country;
    });
    if (!exists) return;
    const p = new URLSearchParams(params);
    p.set("country", country);
    p.set("geo", "1");  // banner-visibility marker
    setParams(p);
    try { sessionStorage.setItem("rentalcar_geo_applied_or_dismissed", "1"); } catch { /* ignore */ }
  }, [cars, params, setParams]);

  const [pendingGeoCountry, setPendingGeoCountry] = useState(null);
  useEffect(() => {
    if (pendingGeoCountry && cars.length > 0) {
      applyGeoIfPossible(pendingGeoCountry);
      setPendingGeoCountry(null);
    }
  }, [cars, pendingGeoCountry, applyGeoIfPossible]);

  const apply = (overrides = {}) => {
    // Iter 70b — accept explicit overrides so callers can pass the "about to
    // become" state after a setState (avoiding stale-closure races on chips
    // whose click handler flips state + writes the URL in the same tick).
    const _pickupLoc      = overrides.pickupLoc      ?? pickupLoc;
    const _dropoffLoc     = overrides.dropoffLoc     ?? dropoffLoc;
    const _startDate      = overrides.startDate      ?? startDate;
    const _endDate        = overrides.endDate        ?? endDate;
    const _carType        = overrides.carType        ?? carType;
    const _trans          = overrides.trans          ?? trans;
    const _fuel           = overrides.fuel           ?? fuel;
    const _seats          = overrides.seats          ?? seats;
    const _price          = overrides.price          ?? price;
    const _petFriendly    = overrides.petFriendly    ?? petFriendly;
    const _smokingAllowed = overrides.smokingAllowed ?? smokingAllowed;
    const _featuredOnly   = overrides.featuredOnly   ?? featuredOnly;
    const _sort           = overrides.sort           ?? sort;

    const next = new URLSearchParams();
    if (_pickupLoc) next.set("pickup_location", _pickupLoc);
    if (_dropoffLoc) next.set("dropoff_location", _dropoffLoc);
    if (_startDate) next.set("start_date", new Date(_startDate).toISOString());
    if (_endDate) next.set("end_date", new Date(_endDate).toISOString());
    if (_carType) next.set("car_type", _carType);
    if (_trans) next.set("transmission", _trans);
    if (_fuel) next.set("fuel_type", _fuel);
    if (_seats) next.set("seats", String(_seats));
    if (_price[0] > 0) next.set("min_price", String(_price[0]));
    if (_price[1] < 500) next.set("max_price", String(_price[1]));
    if (_petFriendly) next.set("pet_friendly", "true");
    if (_smokingAllowed) next.set("smoking_allowed", "true");
    if (_featuredOnly) next.set("featured", "1");
    if (_sort) next.set("sort", _sort);
    // Iter 67: preserve country pick from the hero-stepper round-trip.
    const incomingCountry = params.get("country");
    if (incomingCountry) next.set("country", incomingCountry);
    // Iter 67ac: keep the vendor_id filter sticky across user-side changes so
    // refining seats/fuel doesn't escape the "show me this host's fleet" view.
    const incomingVendor = params.get("vendor_id");
    if (incomingVendor) next.set("vendor_id", incomingVendor);
    setParams(next);
    fetchCars(overrides);
  };

  const reset = () => {
    setPickupLoc(""); setDropoffLoc(""); setStartDate(""); setEndDate("");
    setCarType(""); setTrans(""); setFuel(""); setSeats(0); setPrice([0, 500]);
    setPetFriendly(false); setSmokingAllowed(false); setFeaturedOnly(false);
    setSort("price_asc");
    setQuickPreset(null);
    setParams(new URLSearchParams());
    setTimeout(() => fetchCars("price_asc"), 0);
  };

  // Iter 67m: client-side post-filter for the quick-pick rail. Each preset is a
  // declarative `match(car) => bool` so the funnel composes cleanly with the
  // existing backend-side filters above.
  // Iter 67co — the country chip filter is also applied here so the chip
  // strip can render a stable full-country distribution from the parent
  // `cars` list while still narrowing what's rendered below.
  // Iter 69 — strip now filters by Albanian CITY (`?city=`) instead of
  // country. Albania-only marketplace, so a city-level chip is more useful.
  const visibleCars = useMemo(() => {
    let out = cars;
    const city = params.get("city");
    if (city) {
      out = out.filter((c) => {
        if (_countryFromLocation(c.location) !== "Albania") return false;
        return _cityFromLocation(c.location) === city;
      });
    }
    if (!quickPreset) return out;
    const preset = QUICK_PRESET_BY_ID[quickPreset];
    if (!preset) return out;
    return out.filter((c) => preset.match(c));
  }, [cars, quickPreset, params]);

  const onQuickSelect = (id) => {
    setQuickPreset(id);
    const p = new URLSearchParams(params);
    if (id) p.set("quick", id); else p.delete("quick");
    setParams(p);
  };

  const onSortChange = (next) => {
    setSort(next);
    const p = new URLSearchParams(params);
    p.set("sort", next);
    setParams(p);
    fetchCars(next);
  };

  // Iter 67co → Iter 69 — city chip handler. Pure URL update — filtering
  // happens client-side in `visibleCars` so the chip strip stays stable.
  // Clicking the active chip toggles the filter off.
  const selectedCity = params.get("city");

  // Iter 69 — fetch social-proof signals (recent bookings + viewers-now)
  // for the currently visible cars. One batch round-trip per filter change.
  const visibleCarIds = useMemo(() => visibleCars.map((c) => c.id), [visibleCars]);
  const { getProofFor } = useSocialProof(visibleCarIds);
  const onCitySelect = (city) => {
    const p = new URLSearchParams(params);
    if (!city || city === selectedCity) {
      p.delete("city");
    } else {
      p.set("city", city);
    }
    // Drop legacy params that no longer participate in the chip strip.
    p.delete("country");
    p.delete("geo");
    setParams(p);
    try {
      sessionStorage.setItem("rentalcar_geo_applied_or_dismissed", "1");
    } catch { /* ignore */ }
  };

  return (
    <div>
      <Header />

      {/* Filter bar sits at the very top of the body so it can stick under the
          header without colliding with the page's outer max-w padding. */}
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        {/* Iter 69 — Vendor-scope ribbon. Tells the customer "you're only
            seeing X's cars" + gives a one-click escape. Self-hides when
            no vendor scope is active. */}
        {vendorScopeId && (
          <div
            className="mb-4 rounded-xl bg-amber-50 ring-1 ring-amber-200 px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
            data-testid="vendor-scope-ribbon"
          >
            <div className="flex items-center gap-2 text-sm text-amber-900 min-w-0">
              <Store className="h-4 w-4 text-amber-700 shrink-0" />
              <span className="truncate">
                Showing only <span className="font-bold">{sourceVendor?.name || "this vendor"}</span>'s fleet.
              </span>
            </div>
            <button
              type="button"
              className="text-[11px] font-bold uppercase tracking-wider text-amber-900/70 hover:text-amber-900"
              onClick={clearVendorScope}
              data-testid="vendor-scope-ribbon-clear"
            >
              Show all vendors
            </button>
          </div>
        )}
        {/* Iter 67bs — Partner-referral ribbon. Visible only when the user
            landed via a hotel/agency link. Tells the renter who referred them
            (transparency) + lets the platform attribute the eventual booking. */}
        {partnerRef && (
          <div
            className="mb-4 rounded-xl bg-pink-50 ring-1 ring-pink-200 px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
            data-testid="partner-referral-ribbon"
          >
            <div className="text-sm text-pink-900">
              <span className="font-bold">{t("browse.partnerOnBehalf", { agent: partnerRef.agent?.name })}</span>{" "}
              <span className="text-pink-900/70">— {t("browse.partnerSupports", { vendor: partnerRef.vendor?.name })}</span>
            </div>
            <button
              type="button"
              className="text-[11px] font-bold uppercase tracking-wider text-pink-900/70 hover:text-pink-900"
              onClick={() => {
                // Iter 67fg — clear both old and new key to scrub legacy state.
                try { localStorage.removeItem("rentalcar_partner_ref"); } catch { /* ignore */ }
                try { localStorage.removeItem("drivenest_partner_ref"); } catch { /* ignore */ }
                setPartnerRef(null);
              }}
              data-testid="partner-referral-ribbon-clear"
            >
              {t("browse.partnerClear")}
            </button>
          </div>
        )}
        {/* Iter 67ad — recently-viewed-hosts quick-access strip; self-hides
            for first-time visitors so it never feels empty. */}
        <RecentVendorsStrip className="!px-0 mb-4" />
        <ModifySearchPill />
        {/* Iter 70b — Featured-only chip. Amber to match the homepage badge. */}
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => {
              const next = !featuredOnly;
              setFeaturedOnly(next);
              apply({ featuredOnly: next });
            }}
            aria-pressed={featuredOnly}
            data-testid="browse-featured-only-chip"
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
              featuredOnly
                ? "bg-gradient-to-r from-amber-400 to-yellow-300 text-amber-950 border-amber-500 shadow-md"
                : "bg-white text-neutral-800 border-neutral-200 hover:border-amber-400 hover:text-amber-800"
            }`}
          >
            <Sparkles className={`h-4 w-4 ${featuredOnly ? "text-amber-800" : "text-amber-500"}`} />
            Featured only
            {featuredOnly && (
              <span className="ml-1 text-[10px] uppercase tracking-wider bg-white/70 text-amber-800 rounded-full px-1.5 py-0.5">
                on
              </span>
            )}
          </button>
          {featuredOnly && (
            <span className="text-xs text-neutral-500">
              Showing only the cars featured on the homepage this cycle.
            </span>
          )}
        </div>
        <QuickFilterRail activeId={quickPreset} onSelect={onQuickSelect} />
        <BrowseFilterStrip
          carType={carType} setCarType={setCarType}
          trans={trans} setTrans={setTrans}
          fuel={fuel} setFuel={setFuel}
          seats={seats} setSeats={setSeats}
          price={price} setPrice={setPrice}
          petFriendly={petFriendly} setPetFriendly={setPetFriendly}
          smokingAllowed={smokingAllowed} setSmokingAllowed={setSmokingAllowed}
          resultCount={visibleCars.length}
          onApply={apply}
          onReset={reset}
        />
      </div>

      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {/* Iter 67co → Iter 69 — Albanian-city cluster strip; doubles as a quick city jump-link. */}
        <BrowseCountrySummary
          cars={cars}
          selectedCity={selectedCity}
          onSelect={onCitySelect}
        />

        {/* Header strip: title + sort + view toggle */}
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl lg:text-4xl tracking-tight font-medium">{t("browse.availableCars")}</h1>
            <div className="text-sm text-neutral-500 mt-1 flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-indigo-500" />
              {loading ? t("common.loading") : `${visibleCars.length} ${t("browse.result", { count: visibleCars.length })}`}
              {sort && !loading && visibleCars.length > 0 && (
                <span className="text-neutral-400">· {t("browse.sortedBy", { sort: t(SORT_OPTIONS.find((s) => s.id === sort)?.labelKey || "").toLowerCase() })}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-full border border-border p-0.5 bg-white" role="tablist">
              {SORT_OPTIONS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => onSortChange(s.id)}
                  className={`text-[11px] font-semibold px-3 py-1.5 rounded-full transition ${
                    sort === s.id ? "bg-neutral-900 text-white" : "text-neutral-600 hover:text-neutral-900"
                  }`}
                  data-testid={`browse-sort-${s.id}`}
                >
                  {t(s.labelKey)}
                </button>
              ))}
            </div>
            <div className="inline-flex rounded-full border border-border p-0.5 bg-neutral-50" role="tablist" data-testid="browse-view-toggle">
              <button
                type="button"
                onClick={() => setView("list")}
                className={`inline-flex items-center gap-1 text-[11px] font-medium px-3 py-1.5 rounded-full transition ${view === "list" ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-500"}`}
                data-testid="browse-view-list"
              >
                <LayoutGrid className="h-3.5 w-3.5" /> List
              </button>
              <button
                type="button"
                onClick={() => setView("map")}
                className={`inline-flex items-center gap-1 text-[11px] font-medium px-3 py-1.5 rounded-full transition ${view === "map" ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-500"}`}
                data-testid="browse-view-map"
              >
                <MapIcon className="h-3.5 w-3.5" /> Map
              </button>
            </div>
          </div>
        </div>

        {/* Split-screen REMOVED in Iter 67n — cards now use full width in a
            2-column grid so more cars are visible at once. Map is one click
            away via the header toggle and rendered full-width when shown.
            Iter 69 — on mobile, switching to "map" used to leave the user
            looking at the QuickFilterRail/country chips while the map
            rendered ~900px below the fold; the next visible section was the
            footer. We now scroll the map into view as soon as `view`
            flips, and the map container itself becomes full-bleed on
            mobile so it dominates the viewport. */}
        {view === "map" ? (
          <div
            ref={(node) => {
              if (node && view === "map") {
                // Defer so React has painted the new DOM before we scroll.
                requestAnimationFrame(() => {
                  try {
                    node.scrollIntoView({ block: "start", behavior: "smooth" });
                  } catch {
                    node.scrollIntoView();
                  }
                });
              }
            }}
            className="relative rounded-3xl overflow-hidden border border-border shadow-sm h-[calc(100vh-180px)] sm:h-[70vh] min-h-[480px]"
            data-testid="browse-map-fullwidth"
          >
            {/* Iter 69 — close (×) button so mobile users can return to the
                list view without scrolling all the way down to the sticky
                bottom toggle bar. Hidden on lg+ where the desktop header
                toggle is already visible. */}
            <button
              type="button"
              onClick={() => setView("list")}
              aria-label="Close map and return to list"
              data-testid="browse-map-close-btn"
              className="lg:hidden absolute top-3 right-3 z-[1000] h-10 w-10 rounded-full bg-white shadow-lg ring-1 ring-black/5 flex items-center justify-center text-neutral-900 hover:bg-neutral-50 active:scale-95 transition"
            >
              <XIcon className="h-5 w-5" />
            </button>
            <BrowseMap filteredCarIds={new Set(visibleCars.map((c) => c.id))} highlightedCarId={hoveredId} fill />
          </div>
        ) : (
          <div>
            {loading ? (
              <div className="text-neutral-500">{t("common.loading")}</div>
            ) : visibleCars.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-border p-16 text-center">
                <div className="text-neutral-700 font-medium mb-1">{t("browse.noResults")}</div>
                <div className="text-sm text-neutral-500">{t("browse.noResultsHelp")}</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {visibleCars.map((c, i) => (
                  <BrowseCarCard
                    key={c.id}
                    car={c}
                    index={i}
                    onHover={setHoveredId}
                    proof={getProofFor(c.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/*
        Iter 67cu — Mobile-only sticky bottom bar on Browse.
        Mirrors the CarDetail bottom CTA pattern: stays glued to the bottom of
        the viewport on `< lg`, shows "From €X / day" derived from the cheapest
        visible car, and toggles list <-> map with a single tap so renters
        don't have to scroll back to the top header pill to switch views.
        Self-hides if there's nothing to show (loading, no results, or vendor-
        scoped views where the toggle is irrelevant).
      */}
      {!loading && visibleCars.length > 0 && (() => {
        // Iter 67cv — active filter count for the Filter pill badge. Counts
        // every refinement that's diverged from the "no filter" default so
        // renters get a glanceable signal of how aggressive their current
        // narrowing is.
        const activeFilterCount = [
          carType,
          trans,
          fuel,
          seats > 0,
          price[0] > 0 || price[1] < 500,
          petFriendly,
          smokingAllowed,
        ].filter(Boolean).length;
        const cheapest = Math.min(
          ...visibleCars.map((c) => Number(c.price_per_day) || Infinity).filter((n) => isFinite(n))
        );
        return (
          <>
            <div className="h-20 lg:hidden" aria-hidden="true" />
            <div
              className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-gradient-to-r from-sky-50 via-violet-50 to-pink-50 border-t border-sky-200 shadow-[0_-6px_20px_-10px_rgba(99,102,241,0.45)]"
              style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
              data-testid="browse-mobile-cta-bar"
            >
              <div className="px-3 py-3 flex items-center gap-2">
                <div
                  className="min-w-0 rounded-2xl bg-white/80 backdrop-blur ring-1 ring-sky-200 px-3 py-1.5 shadow-sm"
                  data-testid="browse-mobile-cta-price-card"
                >
                  <div className="text-[9px] uppercase tracking-[0.12em] font-bold text-sky-700 leading-tight">
                    From · {visibleCars.length}
                  </div>
                  <div
                    className="text-base font-bold text-neutral-900 tabular-nums leading-tight"
                    data-testid="browse-mobile-cta-from-price"
                  >
                    <Money amount={cheapest} />
                  </div>
                </div>

                {/* Iter 67cv — Filter pill opens BrowseFilterStrip in a bottom sheet. */}
                <Sheet>
                  <SheetTrigger asChild>
                    <Button
                      size="lg"
                      variant="outline"
                      className="relative rounded-full bg-white/90 backdrop-blur ring-1 ring-sky-300 hover:bg-white text-neutral-900 font-bold px-4 shrink-0 shadow-sm"
                      data-testid="browse-mobile-cta-filter"
                    >
                      <SlidersHorizontal className="h-4 w-4 mr-1.5" />
                      {t("browse.filter")}
                      {activeFilterCount > 0 && (
                        <span
                          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold ring-2 ring-white"
                          data-testid="browse-mobile-cta-filter-badge"
                        >
                          {activeFilterCount}
                        </span>
                      )}
                    </Button>
                  </SheetTrigger>
                  <SheetContent
                    side="bottom"
                    className="h-[88vh] p-0 rounded-t-3xl overflow-hidden flex flex-col"
                    data-testid="browse-mobile-filter-sheet"
                  >
                    <SheetHeader className="px-5 pt-5 pb-3 border-b border-border bg-white sticky top-0 z-10">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-left">
                          <SheetTitle className="text-base">{t("browse.filterResults")}</SheetTitle>
                          <p className="text-xs text-neutral-500 mt-0.5">{t("browse.filterResultsHelp", { count: visibleCars.length })}</p>
                        </div>
                        <SheetClose asChild>
                          <button
                            type="button"
                            aria-label="Close"
                            className="h-9 w-9 rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-700 flex items-center justify-center transition shrink-0"
                            data-testid="browse-mobile-filter-sheet-close"
                          >
                            <XIcon className="h-4 w-4" />
                          </button>
                        </SheetClose>
                      </div>
                    </SheetHeader>
                    <div className="flex-1 overflow-y-auto p-4 pb-4">
                      <BrowseFilterStrip
                        variant="sheet"
                        carType={carType} setCarType={setCarType}
                        trans={trans} setTrans={setTrans}
                        fuel={fuel} setFuel={setFuel}
                        seats={seats} setSeats={setSeats}
                        price={price} setPrice={setPrice}
                        petFriendly={petFriendly} setPetFriendly={setPetFriendly}
                        smokingAllowed={smokingAllowed} setSmokingAllowed={setSmokingAllowed}
                        resultCount={visibleCars.length}
                        onApply={apply}
                        onReset={reset}
                      />
                    </div>
                    {/* Iter 69 — sticky "Show N matching cars" CTA at the
                        bottom of the sheet. Dismisses the sheet on tap so
                        users don't have to scroll back up to the close (×)
                        button after picking filters. */}
                    <div className="border-t border-border bg-white px-4 py-3 shadow-[0_-4px_12px_-6px_rgba(0,0,0,0.08)]">
                      <SheetClose asChild>
                        <Button
                          size="lg"
                          className="w-full rounded-full h-12 font-bold bg-neutral-900 hover:bg-neutral-800 text-amber-400 shadow-md ring-1 ring-amber-500/30 disabled:opacity-50"
                          disabled={visibleCars.length === 0}
                          data-testid="browse-mobile-filter-sheet-apply"
                        >
                          {visibleCars.length === 0
                            ? t("browse.noMatches", { defaultValue: "No matching cars" })
                            : t("browse.showNCars", { count: visibleCars.length, defaultValue: `Show ${visibleCars.length} matching ${visibleCars.length === 1 ? "car" : "cars"}` })}
                        </Button>
                      </SheetClose>
                    </div>
                  </SheetContent>
                </Sheet>

                <Button
                  size="lg"
                  onClick={() => setView(view === "map" ? "list" : "map")}
                  className={`rounded-full font-bold px-4 shrink-0 shadow-md ${
                    view === "map"
                      ? "bg-gradient-to-r from-neutral-900 to-neutral-700 hover:from-neutral-800 hover:to-neutral-600 text-white shadow-neutral-300"
                      : "bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white shadow-orange-200"
                  }`}
                  data-testid="browse-mobile-cta-toggle"
                >
                  {view === "map" ? (
                    <span className="inline-flex items-center gap-1"><ListIcon className="h-4 w-4" /> {t("browse.viewList")}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1"><MapPinIcon className="h-4 w-4" /> {t("browse.viewMap")}</span>
                  )}
                </Button>
              </div>
            </div>
          </>
        );
      })()}

      <Footer />
    </div>
  );
}
